import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Addendum } from './Contract.interface';

interface PostInspectionAddendumModalProps {
  isOpen: boolean;
  toggle: () => void;
  contractId: string;
  onComplete: () => void;
  contractPrice: number;
}

const PostInspectionAddendumModal: React.FC<PostInspectionAddendumModalProps> = ({
  isOpen,
  toggle,
  contractId,
  onComplete,
  contractPrice
}) => {
  const [inspectionOption, setInspectionOption] = useState<'remove' | 'changes' | ''>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [changes, setChanges] = useState('');
  const [newPrice, setNewPrice] = useState(contractPrice.toString());
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setInspectionOption('');
    setShowConfirmation(false);
    setChanges('');
    setNewPrice(contractPrice.toString());
    setError('');
    setIsSubmitting(false);
  };

  const generateContingencyRemovalText = (): string => {
    return `
      PROPERTY INSPECTION CONTINGENCY REMOVAL ADDENDUM

      This Property Inspection Contingency Removal Addendum (the "Addendum") is made and entered into as of ${new Date().toLocaleDateString()}, and is incorporated into and made a part of the Residential Construction Contract (the "Contract") dated [Contract Date].

      The Contractor has completed the property inspection as specified in Paragraph 14 of the Contract and hereby removes the property inspection contingency. The Contractor confirms that:

      1. The property has been thoroughly inspected and assessed for the scope of work outlined in the Contract.
      2. The Contractor understands and accepts the current condition of the property as it relates to the planned work.
      3. The Contractor has not identified any conditions that would prevent the completion of the work as specified in the Contract.
      4. All terms and conditions of the original Contract remain in full force and effect.

      By executing this Addendum, the property inspection contingency described in Paragraph 14 of the Contract is removed, and the Contract becomes binding on both parties.
    `;
  };

  const generateChangesText = (): string => {
    const priceDifference = parseFloat(newPrice) - contractPrice;
    const priceChange = priceDifference >= 0 ? `increase of $${priceDifference.toFixed(2)}` : `decrease of $${Math.abs(priceDifference).toFixed(2)}`;

    return `
      CONTRACT MODIFICATION ADDENDUM BASED ON PROPERTY INSPECTION

      This Contract Modification Addendum (the "Addendum") is made and entered into as of ${new Date().toLocaleDateString()}, and is incorporated into and made a part of the Residential Construction Contract (the "Contract") dated [Contract Date].

      Based on the property inspection completed pursuant to Paragraph 14 of the Contract, the following modifications are required:

      REQUIRED CHANGES:
      ${changes}

      PRICE MODIFICATION:
      Original Contract Price: $${contractPrice.toFixed(2)}
      New Contract Price: $${parseFloat(newPrice).toFixed(2)}
      Price ${priceDifference >= 0 ? 'Increase' : 'Decrease'}: $${Math.abs(priceDifference).toFixed(2)}

      All other terms and conditions of the original Contract remain in full force and effect.
    `;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const firestore = firebase.firestore();
      const contractRef = firestore.collection('contracts').doc(contractId);

      const addendum: Addendum = {
        type: inspectionOption === 'remove' ? 'contingencyRemoval' : 'contractModification',
        date: new Date().toISOString(),
        content: inspectionOption === 'remove' ? generateContingencyRemovalText() : generateChangesText(),
        originalPrice: contractPrice,
        newPrice: parseFloat(newPrice),
        changes: changes,
      };

      await contractRef.update({
        addendums: firebase.firestore.FieldValue.arrayUnion(addendum),
        ...(inspectionOption === 'changes' && { contractPrice: parseFloat(newPrice) })
      });

      resetState();
      toggle();
      onComplete();
    } catch (error) {
      console.error('Error creating addendum:', error);
      setError('Failed to create addendum. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Post-Inspection Action</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        
        {!showConfirmation ? (
          <>
            <FormGroup tag="fieldset">
              <legend>Select Post-Inspection Action</legend>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="inspectionOption"
                    value="remove"
                    checked={inspectionOption === 'remove'}
                    onChange={(e) => setInspectionOption(e.target.value as 'remove' | 'changes')}
                  />
                  Remove Property Inspection Contingency
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="inspectionOption"
                    value="changes"
                    checked={inspectionOption === 'changes'}
                    onChange={(e) => setInspectionOption(e.target.value as 'remove' | 'changes')}
                  />
                  Contract Changes Required
                </Label>
              </FormGroup>
            </FormGroup>

            {inspectionOption === 'changes' && (
              <>
                <FormGroup>
                  <Label for="changes">Required Changes</Label>
                  <Input
                    type="textarea"
                    id="changes"
                    value={changes}
                    onChange={(e) => setChanges(e.target.value)}
                    rows={5}
                    placeholder="Enter detailed description of required changes..."
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="newPrice">New Contract Price</Label>
                  <Input
                    type="number"
                    id="newPrice"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </FormGroup>
              </>
            )}
          </>
        ) : (
          <div className="text-center">
            <h4>Confirm Action</h4>
            <p>
              {inspectionOption === 'remove'
                ? 'Are you sure you want to remove the property inspection contingency? This action cannot be undone.'
                : 'Are you sure you want to submit these contract changes? This will create a contract addendum.'}
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {!showConfirmation ? (
          <>
            <Button color="secondary" onClick={toggle}>Cancel</Button>
            <Button
              color="primary"
              onClick={() => setShowConfirmation(true)}
              disabled={!inspectionOption || (inspectionOption === 'changes' && (!changes || !newPrice))}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button color="secondary" onClick={() => setShowConfirmation(false)}>Back</Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PostInspectionAddendumModal;